import { FC, useState } from "react";
import { Pressable } from "react-native";
import { useSetAtom } from "jotai";
import {
  AlertTriangleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MailIcon,
  XIcon,
} from "lucide-react-native";

import { VnBox } from "@/components/VnBox";
import { ButtonVariantType } from "@/components/VnButton/variants";
import { VnCard } from "@/components/VnCard";
import { VnIcon } from "@/components/VnIcon";
import { VnLink } from "@/components/VnLink";
import { VnText } from "@/components/VnText/VnText";
import { DashboardCardType } from "@/domain/dashboardCards";
import { RedeterminationStatusType } from "@/domain/redeterminations";
import { useTranslator } from "@/hooks/useTranslate";
import { I18nKeyType } from "@/i18n";
import { dismissedDashboardCardIdsAtom } from "@/stores/atoms";
import { formatLongDate } from "@/utils/date";
import { cn } from "@/utils/tailwind";

import { DashboardCompactCard } from "./DashboardCompactCard";

type RenewalGroupCardVariantType = Extract<
  DashboardCardType,
  { kind: "RENEWAL_GROUP" }
>;

type RenewalGroupCardPropsType = {
  card: RenewalGroupCardVariantType;
  isPrimary: boolean;
};

// CALL_BACK renders without an icon (see below); NOT_STARTED uses a warning,
// everything else a mail glyph.
const statusIcon = (status: RedeterminationStatusType) =>
  status === "NOT_STARTED" ? AlertTriangleIcon : MailIcon;

// CALL_BACK is informational (dismiss only) — no action button.
const actionI18nKey = (
  status: RedeterminationStatusType,
): I18nKeyType | null => {
  switch (status) {
    case "NOT_STARTED":
      return "native:redeterminations.startRenewal";
    case "PENDING_FORM":
      return "native:redeterminations.updateStatus";
    default:
      return null;
  }
};

const RenewalActionLink: FC<{
  redeterminationId: string;
  i18nKey: I18nKeyType;
  variant?: ButtonVariantType;
}> = ({ redeterminationId, i18nKey, variant }) => (
  <VnLink
    href={{
      pathname: "/(authed)/(modal)/redetermination/[id]",
      params: { id: redeterminationId },
    }}
    renderAsButton
    i18nKey={i18nKey}
    buttonProps={{ variant }}
  />
);

export const RenewalGroupCard: FC<RenewalGroupCardPropsType> = ({
  card,
  isPrimary,
}) => {
  const { t } = useTranslator();
  const setDismissedIds = useSetAtom(dismissedDashboardCardIdsAtom);
  const [isExpanded, setIsExpanded] = useState(false);

  const { status, items, isDismissible, id } = card;
  const isAggregated = items.length > 1;
  const StatusIcon = statusIcon(status);
  const actionKey = actionI18nKey(status);
  const [firstItem] = items;

  // A renewal group card is only built with at least one item.
  if (!firstItem) {
    return null;
  }

  // Aggregated cards with a per-item action (start/form) expand into rows;
  // those without one (call-back) concatenate every due date into the title.
  const isExpandable = isAggregated && Boolean(actionKey);

  // A demoted single renewal (not the top card) uses the compact card,
  // matching the eligibility/app cards.
  if (!isPrimary && !isAggregated && actionKey) {
    // NOT_STARTED renewals always render primary (see PatientDashboardScreen),
    // so only PENDING_FORM has compact copy; the full sentence is a fallback.
    const compactSummary = t(
      ["native:redeterminations.cardCompact", "native:redeterminations.card"],
      {
        context: status,
        date: formatLongDate(firstItem.redeterminationDate),
      },
    );

    return (
      <DashboardCompactCard
        actions={
          <RenewalActionLink
            redeterminationId={firstItem.id}
            i18nKey={actionKey}
            variant="outline"
          />
        }>
        {compactSummary}
      </DashboardCompactCard>
    );
  }

  const hasIcon = status !== "CALL_BACK";

  // Natural-language join of the distinct due dates: "A", "A and B",
  // "A, B and C" — deduped so renewals sharing a date aren't listed twice.
  const formattedDueDates = [
    ...new Set(items.map(item => formatLongDate(item.redeterminationDate))),
  ];
  const joinedDueDates =
    formattedDueDates.length <= 1
      ? (formattedDueDates[0] ?? "")
      : formattedDueDates.slice(0, -1).join(t("common:listSeparator")) +
        t("common:listConnector") +
        (formattedDueDates.at(-1) ?? "");

  const summary = isAggregated
    ? t("native:redeterminations.cardAggregated", {
        context: status,
        total: items.length,
        dates: joinedDueDates,
      })
    : t("native:redeterminations.card", {
        context: status,
        date: formatLongDate(firstItem.redeterminationDate),
      });

  const handleDismiss = () => {
    setDismissedIds(previous =>
      previous.includes(id) ? previous : [...previous, id],
    );
  };

  return (
    <VnCard className="flex flex-col gap-4">
      <VnBox className="flex flex-row items-center gap-4">
        {hasIcon && (
          <VnBox
            className={cn("rounded-md bg-black/5 p-6", {
              "p-3": isExpandable,
            })}>
            <VnIcon
              as={StatusIcon}
              size={isExpandable ? 28 : 42}
              strokeWidth={1}
            />
          </VnBox>
        )}

        <VnText variant="lg" className="flex-1">
          {summary}
        </VnText>

        {isExpandable && (
          <Pressable
            accessibilityRole="button"
            onPress={() => setIsExpanded(previous => !previous)}>
            <VnIcon as={isExpanded ? ChevronUpIcon : ChevronDownIcon} />
          </Pressable>
        )}

        {isDismissible && (
          <Pressable accessibilityRole="button" onPress={handleDismiss}>
            <VnIcon as={XIcon} />
          </Pressable>
        )}
      </VnBox>

      {/* Primary single: prominent full-width action below the title.
          Expandable aggregate: per-item rows revealed when expanded. */}
      {!isAggregated
        ? actionKey && (
            <RenewalActionLink
              redeterminationId={firstItem.id}
              i18nKey={actionKey}
            />
          )
        : isExpandable &&
          isExpanded &&
          items.map(item => (
            // -mx-5 cancels the card's p-5 so the divider spans the full width.
            <VnBox
              key={item.id}
              className="border-border -mx-5 flex flex-row items-center justify-between gap-4 border-t px-5 pt-4">
              <VnText
                variant="md"
                i18nKey="native:redeterminations.due"
                i18nOptions={{
                  date: formatLongDate(item.redeterminationDate),
                }}
              />

              {actionKey && (
                <RenewalActionLink
                  redeterminationId={item.id}
                  i18nKey={actionKey}
                  variant="outline"
                />
              )}
            </VnBox>
          ))}
    </VnCard>
  );
};
